<template>
    <Detail v-if="data" :logo-on-click="logoOnClick">
        <template v-slot:header>
            <div class="row no-gutters">
                <div class="col col-12">
                    <h2>
                        {{ data.name }}
                        <span class="badge rounded-pill badge-risk" :class="['badge-risk-' + versionRiskLevel(data, version)]"
                            v-b-tooltip.hover :title="hoverTitle(data, version)">
                            {{ $t("risk.levelFull." + versionRiskLevel(data, version)) }}
                        </span>
                    </h2>
                    <h3>{{ $filters.upper($t("organization_id")) }}: {{ data.ico }}</h3>
                </div>
            </div>
        </template>

        <template #back>
            <router-link :to="{ name: 'suppliers', params: { version: version, hash: hash } }" custom v-slot="{ navigate }">
                <button @click="navigate" @keypress.enter="navigate" class="btn btn-inline text-nowrap d-flex align-items-center">
                    <b-icon icon="arrow-left" />&nbsp;{{ $t("supplierDetail.nav.suppliersList") }}
                </button>
            </router-link>
        </template>

        <template #breadcrumbs>
            <div>
                <router-link :to="{ name: version, params: { hash: hash } }">{{ data.analysis.name }}</router-link>
                &nbsp;/&nbsp;
                <router-link :to="{ name: 'suppliers', params: { version: version, hash: hash } }">{{
                    $t("overview.suppliers.title")
                }}</router-link>
                &nbsp;/&nbsp;
                {{ data.name }}
            </div>
        </template>

        <template v-slot:header_small>
            <div class="row no-gutters">
                <div class="col col-12">
                    <h2>
                        {{ data.name }}
                        <span class="badge rounded-pill badge-risk"
                            :class="['badge-risk-' + versionRiskLevel(data, version)]">
                            {{ $t("risk.levelFull." + versionRiskLevel(data, version)) }}
                        </span>
                    </h2>
                </div>
            </div>
        </template>

        <template v-slot:menu>
            <Menu :nav="navigation" :current="currentNav" :hash="hash" :version="version"
                @force-nav="(n) => (forceNav = n)" />
        </template>

        <template v-slot:content>
            <section id="overview_section">
                <Summary v-bind="overviewSettings">
                    <template v-slot:value(vestnik)="item">{{ $filters.price(item.value) }}</template>
                    <template v-slot:value(profil)="item">{{ $filters.price(item.value) }}</template>
                    <template v-slot:value(rsmluv)="item">{{ $filters.price(item.value) }}</template>
                    <template v-slot:value(esmluv)="item">{{ $filters.price(item.value) }}</template>
                    <template v-slot:value(payments)="item">{{ $filters.price(item.value) }}</template>
                    <template v-slot:value(years)="item"> {{ $filters.integer(item.value[0]) }} - {{
                        $filters.integer(item.value[1])
                    }}
                    </template>
                    <template v-slot:value(won)="item">{{ formatPercentage(item.value) }}</template>
                    <template v-slot:value(bids)="item">{{ formatNumber(item.value, 2) }}</template>
                </Summary>
            </section>

            <section id="flat_flags_section">
                <h4 class="section-title">
                    {{ $t("supplierDetail.nav.flags") }}
                </h4>

                <EmptySection v-show="!hasFlatFlagsData && !hasOtherFlagsData"
                    :message="$t('supplierDetail.flags.nodata')" />

                <div v-show="hasFlatFlagsData">
                    <div class="row align-items-end">
                        <div class="col col-1 flag_header">
                            {{ $t("supplierDetail.flags.header.risk") }}
                        </div>
                        <div class="col col-3 flag_header">
                            {{ $t("supplierDetail.flags.header.group") }}
                        </div>
                        <div class="col flag_header">
                            {{ $t("supplierDetail.flags.header.name") }}
                        </div>
                    </div>

                    <div v-for="(f, i) in flatFlags" :key="'flat-flag-' + i" class="row align-items-center">
                        <div class="col col-12">
                            <SupplierFlatFlag :flag="f" :version="version" />
                        </div>
                    </div>
                </div>
                <div v-if="otherSupplierFlagSettings" class="row other-flags">
                    <div class="col col-12">
                        <SupplierFlatFlag v-bind="otherSupplierFlagSettings" />
                    </div>
                </div>

            </section>

            <section id="won_section" v-show="isSectionEnabled('won')">
                <h4 class="section-title">
                    {{ $t("supplierDetail.nav.won") }}
                </h4>

                <TenderTable :table-settings="wonTableSettings" :sort-options="wonTendersSortOptions"
                    :analysis-version="version" :analysis-hash="hash" :supplier-id="data.id" :debounce="filterDebounce"
                    ref="won_tenders_data_table"/>
            </section>

            <section id="lost_section" v-show="isSectionEnabled('lost')">
                <h4 class="section-title">
                    {{ $t("supplierDetail.nav.lost") }}
                </h4>

                <TenderTable :table-settings="lostTableSettings" :sort-options="lostTendersSortOptions"
                    :analysis-version="version" :analysis-hash="hash" :supplier-id="data.id" :debounce="filterDebounce"
                    ref="lost_tenders_data_table" />
            </section>

            <section id="contracts_section" v-show="isSectionEnabled('contracts')">
                <h4 class="section-title">
                    {{ $t("supplierDetail.nav.contracts") }}
                </h4>

                <EmptySection v-show="!hasContractsData" :message="$t('supplierDetail.contracts.nodata')"></EmptySection>

                <div v-show="hasContractsData">
                    <SupplierDetailCard v-if="showContractsHistogram" class="mb-4"
                        :title="$t('supplierDetail.contracts.histogram.title')"
                        :description="$t('supplierDetail.contracts.histogram.description')">
                        <template #content>
                            <Histogram :data="contractsHistogram" />
                        </template>
                    </SupplierDetailCard>

                    <SupplierDetailTable :table-settings="contractsTableSettings" :debounce="filterDebounce"
                        :sort-options="contractsSortOptions" ref="contracts_data_table"
                        :link="getContractRoute"
                        @row-clicked="(item) => {
                            $router.push(getContractRoute(item));
                        }
                            ">
                        <template v-slot:data_risk="row">
                            <RiskLevelDumpling :value="row.item.risk" :level="versionRiskLevel(row.item, version)" />
                        </template>

                        <template v-slot:data_title="row">
                            {{ row.item.title }}
                            <ContractSourceLink :source="row.item.source_url" />
                        </template>

                        <template v-slot:data_signature_date="row">{{ $filters.formatDate(row.item.signature_date, "D. M. YYYY")
                        }}</template>

                        <template v-slot:data_robust_price_vat="row">{{ formatNumber(row.item.robust_price_vat)}}</template>

                        <template v-slot:data_flags="row">
                            <FlagDetailRowToggler :row="row" :callback="toggleContractRowDetail" />
                        </template>

                        <template v-slot:row_details="row">
                            <FlagDetailRow :row="row" :version="version" />
                        </template>
                    </SupplierDetailTable>
                </div>
            </section>

            <section id="icontracts_section" v-show="isSectionEnabled('icontracts')">
                <h4 class="section-title">
                    {{ $t("supplierDetail.nav.icontracts") }}
                </h4>

                <EmptySection v-show="!hasIContractsData" :message="$t('supplierDetail.contracts.nodata')"></EmptySection>
                <div v-show="hasIContractsData">

                    <SupplierDetailCard v-if="showIContractsHistogram" class="mb-4"
                        :title="$t('supplierDetail.icontracts.histogram.title')"
                        :description="$t('supplierDetail.icontracts.histogram.description')">
                        <template #content>
                            <Histogram :data="icontractsHistogram" />
                        </template>
                    </SupplierDetailCard>

                    <SupplierDetailTable :table-settings="icontractsTableSettings" :debounce="filterDebounce"
                        :link="getContractRoute"
                        :sort-options="contractsSortOptions" ref="icontracts_data_table" @row-clicked="(item) => {
                            $router.push(getContractRoute(item));
                        }
                            ">
                        <template v-slot:data_risk="row">
                            <RiskLevelDumpling :value="row.item.risk" :level="versionRiskLevel(row.item, version)" />
                        </template>

                        <template v-slot:data_title="row">
                            {{ row.item.title }}
                            <ContractSourceLink :source="row.item.source_url" />
                        </template>

                        <template v-slot:data_signature_date="row">{{ $filters.formatDate(row.item.signature_date, "D. M. YYYY")
                        }}</template>

                        <template v-slot:data_robust_price_vat="row">{{ formatNumber(row.item.robust_price_vat)}}</template>

                        <template v-slot:data_flags="row">
                            <FlagDetailRowToggler :row="row" :callback="toggleContractRowDetail" />
                        </template>

                        <template v-slot:row_details="row">
                            <FlagDetailRow :row="row" :version="version" />
                        </template>
                    </SupplierDetailTable>
                </div>
            </section>

            <section id="concurrency_section" v-show="isSectionEnabled('concurrency')">
                <h4 class="section-title">
                    {{ $t("supplierDetail.nav.concurrency") }}
                </h4>

                <SupplierDetailCard
                    v-if="Math.max(...Object.values(data.concurrency_histogram)) > 0 && showConcurrencyHistogram"
                    :title="$t('supplierDetail.concurrency.histogram.title')"
                    :description="$t('supplierDetail.concurrency.histogram.description')">
                    <template #content>
                        <Histogram :data="concurrencyHistogram" ref="concurrency_histogram" />
                    </template>
                </SupplierDetailCard>

                <SupplierDetailTable :table-settings="concurrencyTableSettings" :debounce="filterDebounce"
                    :sort-options="contractsSortOptions" ref="concurrency_data_table" :enable-search="false"
                    :enable-sort="false" :enable-pagination="false" :enable-total-rows="false" @row-clicked="(item) => {
                        if (item.is_supplier) {
                            $router.push(getSupplierRoute(item));
                        }
                    }
                        ">

                    <template v-slot:data_concurrents="row">
                        <div class="row">
                            <div class="col-4 align-items-end d-flex">
                                <b>{{ row.item.name }}</b>
                            </div>
                            <div class="col-4 align-items-end d-flex">
                                <b class="d-inline-block text-truncate">{{ data.name }}</b>
                            </div>
                            <div class="col-4 align-items-end d-flex">
                                <b>{{ $t("supplierDetail.concurrency.table.text.otherWins") }}</b>
                            </div>

                            <div class="col-4 align-items-start d-flex">
                                <ConcurrencyCell :total="row.item.total" :number="row.item.won"
                                    :price_number="row.item.won_price" :color="'red'" />
                            </div>
                            <div class="col-4 align-items-start d-flex">
                                <ConcurrencyCell :total="row.item.total" :number="row.item.lost"
                                    :price_number="row.item.lost_price" :color="'#00000055'" />
                            </div>

                            <div class="col-4 align-items-start d-flex">
                                <ConcurrencyCell :total="row.item.total"
                                    :number="row.item.total - row.item.won - row.item.lost"
                                    :price_number="row.item.total_price - row.item.won_price - row.item.lost_price"
                                    :color="'#437C90'" />
                            </div>
                        </div>
                    </template>


                    <template v-slot:data_flags_count="row">
                        <span class="badge rounded-pill bg-danger">{{ row.item.flags_count }}</span>
                    </template>

                    <template v-slot:data_flags="row">
                        <FlagDetailRowToggler :row="row" :callback="toggleConcurrencyRowDetail" />
                    </template>

                    <template v-slot:row_details="row">
                        <FlagDetailRow :row="row" :version="version" />
                    </template>
                </SupplierDetailTable>
            </section>

            <section id="payments_section" v-show="isSectionEnabled('payments')">
                <InvoiceTable :version="version" :hash="hash" :externalData="data" :typeId="data.id" type="supplier" />
            </section>

            <section id="nodata_section" v-show="isSectionEnabled('nodata')">
                <h4 class="section-title">
                    {{ $t("supplierDetail.nav.nodata") }}
                </h4>
                <EmptySection :message="$t('supplierDetail.nodata.description', nodataSections)"></EmptySection>
            </section>
        </template>
    </Detail>
</template>

<script>
import Detail from "@/views/layouts/Detail.vue";
import formattersMixin from "@/plugins/formattersMixin.js";
import Menu from "@/components/Menu.vue";
import Summary from "@/components/Summary.vue";
import RiskLevelDumpling from "@/components/RiskLevelDumpling.vue";
import TenderTable from "@/components/TenderTable.vue";
import SupplierDetailTable from "@/components/SupplierDetailTable.vue";
import Histogram from "@/components/Histogram.vue";
import SupplierDetailCard from "@/components/SupplierDetailCard.vue";
import EmptySection from "@/components/EmptySection.vue";
import FlagDetailRow from "@/components/FlagDetailRow.vue";
import FlagDetailRowToggler from "@/components/FlagDetailRowToggler.vue";
import SupplierFlatFlag from "@/components/SupplierFlatFlag.vue";
import ContractSourceLink from "@/components/ContractSourceLink.vue";
import riskMixin from "@/plugins/riskMixin.js";
import dataTableMixin from "@/plugins/dataTableMixin.js";
import tableSettingsMixin from "@/plugins/tableSettingsMixin.js";
import analysisMixin from "@/plugins/analysisMixin.js";
import InvoiceTable from "@/components/InvoiceTable.vue";
import ConcurrencyCell from "@/components/ConcurrencyCell.vue";

export default {
    props: {
        slug: { required: true },
        version: { required: true },
        hash: { required: true }
    },
    name: "supplier-detail",
    components: {
        Detail,
        Menu,
        Summary,
        RiskLevelDumpling,
        TenderTable,
        SupplierDetailTable,
        Histogram,
        SupplierDetailCard,
        EmptySection,
        FlagDetailRow,
        FlagDetailRowToggler,
        SupplierFlatFlag,
        ContractSourceLink,
        InvoiceTable,
        ConcurrencyCell
    },
    mixins: [formattersMixin, riskMixin, dataTableMixin, tableSettingsMixin, analysisMixin],
    created: function () {
        this.init();
    },
    data: function () {
        return {
            data: null,
            showTenderInvoice: false,
            nav: {
                overview: {
                    scrollTo: {
                        el: "#overview_section",
                        offset: -200,
                        onStart: () => (this.currentNav = this.forceNav),
                        onDone: () => (this.forceNav = null)
                    },
                    label: this.$t("supplierDetail.nav.overview")
                },
                flatFlags: {
                    scrollTo: {
                        el: "#flat_flags_section",
                        onStart: () => (this.currentNav = this.forceNav),
                        onDone: () => (this.forceNav = null)
                    },
                    label: this.$t("supplierDetail.nav.flags")
                },
                won: {
                    scrollTo: {
                        el: "#won_section",
                        onStart: () => (this.currentNav = this.forceNav),
                        onDone: () => (this.forceNav = null)
                    },
                    label: this.$t("supplierDetail.nav.won"),
                    isVisible: () => this.data.won_count > 0,
                    nodataSection: true
                },
                lost: {
                    scrollTo: {
                        el: "#lost_section",
                        onStart: () => (this.currentNav = this.forceNav),
                        onDone: () => (this.forceNav = null)
                    },
                    label: this.$t("supplierDetail.nav.lost"),
                    isVisible: () => this.data.lost_count > 0,
                    nodataSection: true
                },
                contracts: {
                    scrollTo: {
                        el: "#contracts_section",
                        onStart: () => (this.currentNav = this.forceNav),
                        onDone: () => (this.forceNav = null)
                    },
                    label: this.$t("supplierDetail.nav.contracts"),
                    isVisible: () => !!this.data.analysis.start_date_rsmluv
                },
                icontracts: {
                    scrollTo: {
                        el: "#icontracts_section",
                        onStart: () => (this.currentNav = this.forceNav),
                        onDone: () => (this.forceNav = null)
                    },
                    label: this.$t("supplierDetail.nav.icontracts"),
                    isVisible: () => !!this.data.analysis.start_date_esmluv,
                    requiredVersion: "full"
                },
                concurrency: {
                    scrollTo: {
                        el: "#concurrency_section",
                        onStart: () => (this.currentNav = this.forceNav),
                        onDone: () => (this.forceNav = null)
                    },
                    label: this.$t("supplierDetail.nav.concurrency"),
                    isVisible: () => this.data.concurrency && this.data.concurrency.length > 0,
                    nodataSection: true
                },
                payments: {
                    scrollTo: {
                        el: "#payments_section",
                        onStart: () => (this.currentNav = this.forceNav),
                        onDone: () => (this.forceNav = null)
                    },
                    label: this.$t("supplierDetail.nav.payments"),
                    isVisible: () => !!this.data.analysis.start_date_invoice,
                    requiredVersion: "full"
                },
                nodata: {
                    scrollTo: {
                        el: "#nodata_section",
                        onStart: () => (this.currentNav = this.forceNav),
                        onDone: () => (this.forceNav = null)
                    },
                    label: this.$t("supplierDetail.nav.nodata"),
                    isVisible: () => false
                }
            },
            currentNav: "overview",
            forceNav: null,
            flags: [],
            detailsLoaded: false,
            overviewSettings: {
                title: "Přehled",
                description: "Objemy zakázek a smluv dodavatele podle jednotlivých zdrojů.",
                data: []
            },
            flatFlags: [],
            navigation: {},
            nodata: {},
            otherFlags: []
        };
    },
    watch: {
        nodata: function (value) {
            if (!Object.keys(value).length) {
                delete this.navigation["nodata"];
            } else {
                this.navigation["nodata"] = this.nav.nodata;
            }
        },
        "$route.params.slug": function () {
            this.data = null
            this.flatFlags = []
            this.navigation = {}
            this.nodata = {}
            this.otherFlags = []

            this.init()
                .then(() => {
                    this.$refs.won_tenders_data_table.refresh()
                    this.$refs.lost_tenders_data_table.refresh()
                    this.$refs.contracts_data_table.refresh()
                    this.$refs.icontracts_data_table.refresh()
                    this.$refs.payments_data_table.refresh()
                })

            this.currentNav = "overview"
        }
    },
    computed: {
        hasIContractsData: function () {
            return Math.max(...Object.values(this.data.icontracts_histogram)) > 0;
        },
        hasContractsData: function () {
            return Math.max(...Object.values(this.data.contracts_histogram)) > 0;
        },
        hasFlatFlagsData: function () {
            return this.flatFlags.length;
        },
        hasOtherFlagsData: function () {
            return this.otherFlags.length;
        },
        filterDebounce: () => 700,
        concurrencyHistogram: function () {
            if (!this.data.concurrency_histogram) {
                return [];
            }

            return Object.keys(this.data.concurrency_histogram).map((k) => {
                return {
                    label: this.$t("supplierDetail.concurrency.histogram.label." + k),
                    value: this.data.concurrency_histogram[k]
                };
            });
        },
        showConcurrencyHistogram: function () {
            if (!this.data.concurrency_histogram) {
                return false;
            }
            if (this.concurrencyHistogram.reduce((sum, b) => sum + b.value, 0) < 6) {
                return false;
            }
            return true;
        },
        contractsHistogram: function () {
            if (!this.data.contracts_histogram) {
                return [];
            }

            return Object.keys(this.data.contracts_histogram).map((k) => {
                return {
                    label: this.$t("supplierDetail.contracts.histogram.label." + k),
                    value: this.data.contracts_histogram[k]
                };
            });
        },
        showContractsHistogram: function () {
            if (!this.data.contracts_histogram) {
                return false;
            }
            if (this.contractsHistogram.reduce((sum, b) => sum + b.value, 0) < 6) {
                return false;
            }

            return true;
        },
        icontractsHistogram: function () {
            if (!this.data.icontracts_histogram) {
                return [];
            }

            return Object.keys(this.data.icontracts_histogram).map((k) => {
                return {
                    label: this.$t("supplierDetail.contracts.histogram.label." + k),
                    value: this.data.icontracts_histogram[k]
                };
            });
        },
        showIContractsHistogram: function () {
            if (!this.data.icontracts_histogram) {
                return false;
            }
            if (this.icontractsHistogram.reduce((sum, b) => sum + b.value, 0) < 6) {
                return false;
            }

            return true;
        },
        nodataSections: function () {
            return {
                sections: Object.values(this.nodata)
                    .map((n) => n.label)
                    .join(", ")
            };
        },
        otherSupplierFlagSettings: function () {
            if (!this.otherFlags.length) {
                return null
            }

            var worst = this.otherFlags.reduce((max, flag) => {
                return max.risk > flag.risk ? max : flag;
            })

            let settings = {
                flag: {
                    flag_type_group_name: this.$t('supplierDetail.flags.other.group'),
                    flag_type_name: this.$t('supplierDetail.flags.other.name', { count: this.otherFlags.length }),
                    description: this.$t('supplierDetail.flags.other.desc'),
                    risk: worst.risk
                },
                version: this.version
            }

            settings.flag[`risk_level_${this.version}`] = worst[`risk_level_${this.version}`]

            return settings
        }
    },
    methods: {
        init: function () {
            return new Promise((resolve, reject) => {
                this.loadData()
                    .then(() => {
                        this.overviewSettings.data = this.buildOverviewData();

                        this.loadOtherFlags();

                        this.loadFlatFlags();

                        this.buildNavigation();

                        window.removeEventListener("scroll", this.onScroll);
                        window.addEventListener("scroll", this.onScroll);

                        this.$root.$emit("set-page-title", this.$t("titleDetail", { name: this.data.name }))

                        resolve()
                    })
                    .catch(e => reject(e))
            })
        },
        onScroll: function () {
            if (this.forceNav) {
                return;
            }

            Object.keys(this.navigation).forEach((k) => {
                var n = this.navigation[k];

                if (n.scrollTo) {
                    var el = document.querySelector(n.scrollTo.el);
                    if (el) {
                        var top = el.getBoundingClientRect().top;
                        if (top > 0 && top <= 100) {
                            this.currentNav = k;
                        }
                    }
                }
            });
        },
        loadData: function () {
            return new Promise((resolve, reject) => {
                var self = this;

                var params = {
                    format: "json"
                };

                params[`${this.version}_hash`] = this.hash;

                this.$store.getters.api
                    .get("supplier/" + this.slug, { params: params })
                    .then((response) => {
                        self.data = response.data;
                        resolve();
                    })
                    .catch((e) => {
                        reject(e);
                        self.data = null;
                    });
            });
        },
        loadTenderFlags: function (tenderId) {
            return new Promise((resolve, reject) => {
                var params = {
                    format: "json",
                    supplier: this.data.id,
                    tender: tenderId
                };

                params[`${this.version}_hash`] = this.hash;

                this.$store.getters.api
                    .get("flag/", { params: params })
                    .then((response) => {
                        resolve(response.data.data);
                    })
                    .catch((e) => {
                        reject(e);
                    });
            });
        },
        wonTableDataProvider: function (ctx) {
            var params = this.initRequestParams(ctx);

            params["supplier"] = this.data.id;
            params[`${this.version}_hash`] = this.hash;

            return this.$store.getters.api
                .get("tender/", { params: params })
                .then((response) => {
                    var data = response.data.data;

                    this.$refs.won_tenders_data_table.setTotalRows(response.data.count);

                    return data;
                })
                .catch((e) => {
                    console.dir(e);
                    return [];
                });
        },
        lostTableDataProvider: function (ctx) {
            var params = this.initRequestParams(ctx);

            params["bidder"] = this.data.id;
            params[`${this.version}_hash`] = this.hash;

            return this.$store.getters.api
                .get("tender/", { params: params })
                .then((response) => {
                    var data = response.data.data;

                    this.$refs.lost_tenders_data_table.setTotalRows(response.data.count);

                    return data;
                })
                .catch((e) => {
                    console.dir(e);
                    return [];
                });
        },
        loadFlatFlags: function () {
            var params = {
                format: "json",
                supplier: this.data.id,
                flag_level: "supplier,analysis_supplier",
                order_by: "-risk",
                limit: 0
            };

            params[`${this.version}_hash`] = this.hash;

            return this.$store.getters.api
                .get("flag/", { params: params })
                .then((response) => {
                    this.flatFlags = response.data.data;
                })
                .catch((e) => {
                    console.dir(e);
                    this.flatFlags = [];
                });
        },
        onNav: function (value) {
            this.$scrollTo(this.nav[value.section].scrollTo.el, "body", this.nav[value.section].scrollTo);
        },
        contractsTableDataProvider: function (ctx) {
            var params = this.initRequestParams(ctx);

            params["src"] = "rsmluv";
            params["supplier"] = this.data.id;
            params[`${this.version}_hash`] = this.hash;

            return this.$store.getters.api
                .get("contract/", { params: params })
                .then((response) => {
                    var data = response.data.data;
                    this.$refs.contracts_data_table.setTotalRows(response.data.count);
                    return data;
                })
                .catch((e) => {
                    console.dir(e);
                    return [];
                });
        },
        icontractsTableDataProvider: function (ctx) {
            var params = this.initRequestParams(ctx);

            params["src_not"] = "rsmluv";
            params["supplier"] = this.data.id;
            params[`${this.version}_hash`] = this.hash;

            return this.$store.getters.api
                .get("contract/", { params: params })
                .then((response) => {
                    var data = response.data.data;
                    this.$refs.icontracts_data_table.setTotalRows(response.data.count);
                    return data;
                })
                .catch((e) => {
                    console.dir(e);
                    return [];
                });
        },
        toggleConcurrencyRowDetail: function (row) {
            row.item.toggleDetails();
            // first displaying of details starts ajax
            if (row.item.flags === undefined) {
                this.loadConcurrencyFlags(row.item.id)
                    .then((flags) => (row.item.flags = flags))
                    .catch((e) => {
                        console.log(e);
                        row.item.flags = [];
                    })
                    .finally(() => {
                        // little hack - details slot template doesn't react on data changes,
                        // double toggle refreshes data
                        row.item.toggleDetails(); // hide
                        row.item.toggleDetails(); // show again
                    });
            }
        },
        loadConcurrencyFlags: function (id) {
            return new Promise((resolve, reject) => {
                var params = {
                    format: "json",
                    bidder: id,
                    supplier: this.data.id
                };

                params[`${this.version}_hash`] = this.hash;

                this.$store.getters.api
                    .get("competitor_flag/", { params: params })
                    .then((response) => {
                        resolve(response.data.data);
                    })
                    .catch((e) => {
                        reject(e);
                    });
            });
        },
        toggleContractRowDetail: function (row) {
            row.item.toggleDetails();
            // first displaying of details starts ajax
            if (row.item.flags === undefined) {
                this.loadContractFlags(row.item.id)
                    .then((flags) => (row.item.flags = flags))
                    .catch((e) => {
                        console.log(e);
                        row.item.flags = [];
                    })
                    .finally(() => {
                        // little hack - details slot template doesn't react on data changes,
                        // double toggle refreshes data
                        row.item.toggleDetails(); // hide
                        row.item.toggleDetails(); // show again
                    });
            }
        },
        loadContractFlags: function (id) {
            return new Promise((resolve, reject) => {
                var params = {
                    format: "json",
                    supplier: this.data.id,
                    contract: id
                };

                params[`${this.version}_hash`] = this.hash;

                this.$store.getters.api
                    .get("flag/", { params: params })
                    .then((response) => {
                        resolve(response.data.data);
                    })
                    .catch((e) => {
                        reject(e);
                    });
            });
        },
        isSectionEnabled: function (section) {
            return Object.prototype.hasOwnProperty.call(this.navigation, section);
        },
        buildNavigation: function () {
            this.navigation = {};
            this.nodata = {};
            Object.keys(this.nav).forEach((n) => {
                let nav = { ...this.nav[n] };

                var verified = true
                if (!this.verifyVersion(this.version, nav.requiredVersion)) {
                    delete nav["scrollTo"];
                    delete nav["to"];
                    verified = false
                }

                if (!Object.prototype.hasOwnProperty.call(nav, "isVisible") || nav.isVisible()) {
                    this.navigation[n] = nav;
                } else if (nav.nodataSection && verified) {
                    this.nodata[n] = nav;
                }
            });
        },
        buildOverviewData: function () {
            return {
                vestnik: {
                    label: this.$t("supplierDetail.overviewSummary.vestnikBidSum", [this.formatNumber(2000000)]),
                    value: this.data.vestnik_bid_sum
                },
                profil: {
                    label: this.$t("supplierDetail.overviewSummary.profilBidSum", [this.formatNumber(500000)]),
                    value: this.data.profile_bid_sum
                },
                rsmluv: {
                    label: this.$t("supplierDetail.overviewSummary.rsmluvBidSum", [this.formatNumber(50000)]),
                    value: this.data.rsmluv_bid_sum
                },
                esmluv: {
                    label: this.$t("supplierDetail.overviewSummary.esmluvBidSum", [this.formatNumber(50000)]),
                    value: this.data.esmluv_bid_sum,
                    isVisible: () => !!this.data.analysis.start_date_esmluv
                },
                payments: {
                    label: this.$t("supplierDetail.overviewSummary.accountingPayments"),
                    value: this.data.accounting_payments,
                    isVisible: () => !!this.data.analysis.start_date_invoice
                },
                bids: {
                    label: this.$t("supplierDetail.overviewSummary.bidsCountAvg"),
                    value: this.data.bids_count_avg,
                    isVisible: () => this.data.won_count > 0
                },
                won: {
                    label: this.$t("supplierDetail.overviewSummary.wonRatio"),
                    value: this.data.won_count / (this.data.won_count + this.data.lost_count),
                    isVisible: () => this.data.won_count > 0
                },
                years: {
                    label: this.$t("supplierDetail.overviewSummary.fromTo"),
                    value: [this.data.first_year, this.data.last_year]
                }
            };
        },
        loadOtherFlags: function () {
            var params = {
                format: "json",
                supplier: this.data.id,
                flag_level_not: "supplier,analysis_supplier",
                limit: 0
            };

            params[`${this.version}_hash`] = this.hash;

            return this.$store.getters.api
                .get("flag/", { params: params })
                .then((response) => {
                    this.otherFlags = response.data.data
                })
                .catch((e) => {
                    console.log(e)
                    this.otherFlags = []
                });
        },
        logoOnClick: function () {
            this.$router.push({ name: this.version, params: { hash: this.hash } })
        },
        hoverTitle: function (data, version) {
            var level = this.versionRiskLevel(data, version);
            return this.$t(`risk.levelFull.${level}`) + (level != 'no' ? ` ${this.formatPercentage(data.risk, 2)}` : '')
        },
        concurrencyTableDataProvider: function (ctx) {
            console.log(this.$refs)
            setTimeout(function () {
                this.$refs.concurrency_data_table.setTotalRows(this.data.concurrency.length);
            }.bind(this), 300)

            return new Promise((resolve, reject) => {setTimeout(function () {
                resolve(this.data.concurrency.slice((ctx.currentPage - 1)*ctx.perPage, (ctx.currentPage)*ctx.perPage));
            }.bind(this), 300)});
        },
        getContractRoute: function (item) {
            return {
                name: 'contract-detail',
                params: {
                    version: this.version,
                    hash: this.hash,
                    slug: item.slug
                }
            }
        },
        getSupplierRoute: function (item) {
            return {
                name: 'supplier-detail',
                params: {
                    version: this.version,
                    hash: this.hash,
                    slug: item.slug
                }
            }
        }
    }
};
</script>

<style lang="scss">
@import "src/scss/custom";

#menu {
    margin-bottom: 50px;

    button {
        text-align: left;
        background-color: white;
        padding: 0.6rem 1.5rem;
        padding-right: 0;

        &.active {
            background-color: $background-light;
        }

        &:hover {
            background-color: $light;
        }

        &[disabled] {
            background-color: transparent;
        }

        .premium_label {
            float: right;
        }
    }
}


.section-title {
    font-size: 20px;
    font-weight: 700;
    color: $body-color;
    margin-bottom: 20px;
}

#payments_section {
    .b-table-has-details td {
        border-bottom: none;
    }

    .b-table-details {
        background-color: initial;

        td {
            padding-top: 0;
        }
    }
}

.empty-section~.other-flags {
    margin-top: 10px;
}

.supplier-name-column {
    width: 30%;
    min-width: 200px;
}

.two_line_ellipsis {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;


    @supports (-webkit-line-clamp: 2) {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: initial;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
    }
}
</style>
